import type {
  InsightConfigDeleteResponse,
  InsightConfigListParams,
} from '../models/insightApi.types'
import type { InsightConfig, InsightConfigMeta } from '../models/insightConfig.types'
import type { EditableInsightConfigPayload } from '../service/insightConfig'
import { normalizePaginatedResponse } from '../service/normalizeListResponse'

import { cleanQueryParams } from './cleanQueryParams'

import { appHttp, unwrap, type Paginated } from '@/shared/http'

function requestConfig(params: object, signal?: AbortSignal) {
  return {
    params: cleanQueryParams(params),
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

/** Global sysadmin config CRUD with fixed, endpoint-specific unwrapping. */
export class InsightConfigApi {
  private readonly base = '/admin-panel/insights/configs/'

  constructor(private readonly http = appHttp) {}

  async list(
    params: InsightConfigListParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<InsightConfig>> {
    const response = await this.http.get(this.base, requestConfig(params, signal))
    return normalizePaginatedResponse<InsightConfig>(response.data, params)
  }

  async meta(signal?: AbortSignal): Promise<InsightConfigMeta> {
    const response = await this.http.get(`${this.base}meta/`, requestConfig({}, signal))
    return unwrap<InsightConfigMeta>(response)
  }

  async get(configId: number, signal?: AbortSignal): Promise<InsightConfig> {
    const response = await this.http.get(`${this.base}${configId}/`, requestConfig({}, signal))
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async create(
    payload: EditableInsightConfigPayload,
    signal?: AbortSignal,
  ): Promise<InsightConfig> {
    const response = await this.http.post(this.base, payload, requestConfig({}, signal))
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async update(
    configId: number,
    payload: Partial<EditableInsightConfigPayload>,
    signal?: AbortSignal,
  ): Promise<InsightConfig> {
    const response = await this.http.patch(
      `${this.base}${configId}/`,
      payload,
      requestConfig({}, signal),
    )
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async replace(
    configId: number,
    payload: EditableInsightConfigPayload,
    signal?: AbortSignal,
  ): Promise<InsightConfig> {
    const response = await this.http.put(
      `${this.base}${configId}/`,
      payload,
      requestConfig({}, signal),
    )
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async delete(configId: number, signal?: AbortSignal): Promise<InsightConfigDeleteResponse> {
    const response = await this.http.delete(`${this.base}${configId}/`, requestConfig({}, signal))
    return unwrap<InsightConfigDeleteResponse>(response)
  }
}

export const insightConfigApi = new InsightConfigApi()
